import { useState, type ReactNode } from "react";
import { Plus, Minus } from "lucide-react";
import type { CatalogProduct } from "@/lib/catalog/products";
import { readPdpSections } from "@/lib/catalog/pdp-sections";

type Section = {
  id: string;
  title: string;
  render: () => ReactNode;
};

export function InfoAccordion({ product }: { product: CatalogProduct }) {
  const cfg = readPdpSections(product.attributes, product.family);
  const lines = (t: string) => t.split("\n").map((l) => l.trim()).filter(Boolean);

  const all: Section[] = [
    {
      id: "notes",
      title: "Fragrance Notes",
      render: () => (
        <div className="space-y-4 text-sm leading-relaxed text-black/70">
          <p>{product.notes}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            {product.keyNotes.map((n) => (
              <span
                key={n}
                className="border border-[#C6A15B]/50 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#C6A15B]"
              >
                {n}
              </span>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "ingredients",
      title: "Ingredients",
      render: () => (
        <p className="whitespace-pre-line text-sm leading-relaxed text-black/70">{cfg.ingredients.text}</p>
      ),
    },
    {
      id: "shipping",
      title: "Delivery & Shipping",
      render: () => (
        <ul className="space-y-2 text-sm leading-relaxed text-black/70">
          {lines(cfg.shipping.text).map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      ),
    },
    {
      id: "returns",
      title: "Returns",
      render: () => (
        <p className="whitespace-pre-line text-sm leading-relaxed text-black/70">{cfg.returns.text}</p>
      ),
    },
  ];
  const sections = all.filter((s) => { const c = cfg[s.id as keyof typeof cfg]; return c.enabled && (s.id === "notes" || c.text.trim() !== ""); });
  const [open, setOpen] = useState<string | null>(sections[0]?.id ?? null);
  if (sections.length === 0) return null;

  return (
    <div className="divide-y divide-black/10 border-y border-black/10">
      {sections.map((s) => {
        const isOpen = open === s.id;
        return (
          <div key={s.id}>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={`sec-${s.id}`}
              onClick={() => setOpen(isOpen ? null : s.id)}
              className="flex w-full items-center justify-between py-5 text-left text-[11px] font-medium uppercase tracking-[0.32em] text-black transition hover:text-[#C6A15B]"
            >
              <span>{s.title}</span>
              {isOpen ? (
                <Minus className="h-4 w-4" aria-hidden />
              ) : (
                <Plus className="h-4 w-4" aria-hidden />
              )}
            </button>
            {isOpen && (
              <div id={`sec-${s.id}`} className="pb-6">
                {s.render()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
